import React from "react";
import { RotateCcw } from "lucide-react";
import cn from "./cn";
import { Input } from "./FormControls";
import IconButton from "./IconButton";

/**
 * ColorInput — a swatch and a hex field that edit one colour.
 *
 * Built because Settings was hand-rolling the pair: a bare `<input type="color">`
 * beside a bare `<input type="text">`, wired to the same state, twice on one
 * screen — and the theme editor needed five more. Two raw controls per colour is
 * how a screen ends up with its own field spec (TOKEN_CONTRACT §11).
 *
 * ── The `value=""` problem ───────────────────────────────────────────────────
 * A native colour input has no empty state: give it `""` and the browser shows
 * black, which reads as "the colour is black" rather than "nothing is set". So
 * the swatch is driven by `value || fallback` while the hex field shows the real
 * value — an unset colour previews the default it will actually use, and the
 * field stays blank so the caller can tell the two apart.
 *
 * `onChange` receives the hex STRING, not an event: every caller wanted
 * `e.target.value` and one of them is a native colour picker whose event is not
 * interchangeable with the text field's.
 *
 * Props:
 *   value      — `#rrggbb`, or "" for unset
 *   onChange   — (hex: string) => void
 *   fallback   — the colour the swatch shows while unset (the product default)
 *   onReset    — when given, renders a reset control; omit for a required colour
 *   disabled   — greys the whole pair
 *   id         — bound to the swatch, so a <label htmlFor> hits the colour picker
 */
const ColorInput = React.forwardRef(
  (
    {
      value = "",
      onChange,
      fallback = "#000000",
      onReset,
      disabled = false,
      id,
      className = "",
      "aria-label": ariaLabel,
      ...props
    },
    ref
  ) => {
    const swatch = value || fallback;
    const isSet = Boolean(value);

    return (
      <div className={cn("tw-flex tw-items-center tw-gap-2", className)}>
        <input
          ref={ref}
          id={id}
          type="color"
          value={swatch}
          disabled={disabled}
          aria-label={ariaLabel}
          onChange={(e) => onChange?.(e.target.value.toLowerCase())}
          className={cn(
            "tw-h-9 tw-w-11 tw-shrink-0 tw-cursor-pointer tw-rounded-md tw-border tw-border-solid",
            "tw-bg-input tw-p-1 tw-focus-ring",
            isSet ? "tw-border-input-line" : "tw-border-dashed tw-border-line",
            disabled && "tw-cursor-not-allowed tw-opacity-60"
          )}
          {...props}
        />
        <Input
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value.trim().toLowerCase())}
          placeholder={fallback}
          maxLength={7}
          spellCheck={false}
          aria-label={ariaLabel ? `${ariaLabel} hex value` : undefined}
          className="tw-w-[8.5rem] tw-font-mono"
          fullWidth={false}
        />
        {onReset && (
          <IconButton
            size="sm"
            label="Reset to the default colour"
            icon={RotateCcw}
            onClick={onReset}
            disabled={disabled || !isSet}
          />
        )}
      </div>
    );
  }
);
ColorInput.displayName = "ColorInput";

export default ColorInput;
